import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getMeals, getWeekDates, formatDateStr, MealEntry, FOOD_IMAGES } from '../utils/storage';
import './CalendarPage.css';

const DAYS_OF_WEEK_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Helper to get average emotion emoji
export function getEmotionEmoji(before: number, during: number, after: number): string {
  const avg = (before + during + after) / 3;
  if (avg >= 4.5) return '😍';
  if (avg >= 3.5) return '😀';
  if (avg >= 2.5) return '🙂';
  if (avg >= 1.5) return '🙁';
  return '😭';
}

export function CalendarPage() {
  const navigate = useNavigate();
  const weekDates = getWeekDates();
  const meals = getMeals();

  // Group meals by day of week (1 to 7, Mon-Sun)
  // Let's map weekDates to their corresponding meals.
  // Note: getWeekDates returns Mon, Tue, Wed, Thu, Fri, Sat, Sun.
  const groupedMeals = weekDates.map((date) => {
    const dateStr = formatDateStr(date);
    const dayMeals = meals.filter((m) => m.dateStr === dateStr);
    
    // Sort meals by time (rough estimation since it is e.g. "08:15 AM")
    // For sorting, AM/PM comparison is fine
    dayMeals.sort((a, b) => {
      const getMinutes = (t: string) => {
        const [timePart, ampm] = t.split(' ');
        const [h, m] = timePart.split(':').map(Number);
        let hrs = h % 12;
        if (ampm === 'PM') hrs += 12;
        return hrs * 60 + m;
      };
      return getMinutes(a.timestamp) - getMinutes(b.timestamp);
    });

    return {
      date,
      dateStr,
      dayName: DAYS_OF_WEEK_NAMES[date.getDay()],
      dayMeals,
    };
  });

  const todayDateStr = formatDateStr(new Date());
  const visibleDays = groupedMeals.filter(
    (day) => day.dateStr === todayDateStr || day.dayMeals.length > 0
  );

  const handleAddMeal = (dateStr: string, dayIndex: number) => {
    // Navigate to wizard page and pass state
    navigate('/wizard', { state: { targetDateStr: dateStr, targetDayOfWeek: dayIndex } });
  };

  return (
    <div className="calendar-page-container">
      <div className="calendar-header-title">
        <h2>Weekly Meal Diary</h2>
        <p className="calendar-subtitle">Mon to Sun: Track your thoughts & food</p>
      </div>

      <div className="calendar-days-list">
        {visibleDays.map((day) => {
          // day.date.getDay() gives 0 for Sun, 1 for Mon...
          const dayNum = day.date.getDay();
          const isToday = day.dateStr === todayDateStr;

          return (
            <div key={day.dateStr} className="calendar-day-section">
              <div className="calendar-day-header">
                <span className="calendar-day-name">
                  {day.dayName} {isToday ? '(Today)' : ''}
                </span>
                <span className="calendar-day-date">
                  {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>

              <div className="calendar-day-content">
                {day.dayMeals.length > 0 ? (
                  <div className="meals-list">
                    {day.dayMeals.map((meal) => {
                      const avgEmoji = getEmotionEmoji(
                         meal.emotions.before,
                         meal.emotions.during,
                         meal.emotions.after
                      );

                      return (
                        <div
                          key={meal.id}
                          className="meal-card-item"
                          onClick={() => navigate(`/meal/${meal.id}`)}
                        >
                          <div className="meal-card-left">
                            <img
                              src={meal.photoUrl || FOOD_IMAGES[meal.foodType] || FOOD_IMAGES.apple}
                              alt={meal.foodType}
                              className="meal-card-img"
                            />
                          </div>
                          
                          <div className="meal-card-mid">
                            <div className="meal-card-time">{meal.timestamp}</div>
                            <div className="meal-card-name">{meal.customName}</div>
                            <div className="meal-card-tags">
                              <span className="meal-tag-loc">📍 {meal.gps.locationName.split(',')[0]}</span>
                              <span className="meal-tag-social">👥 {meal.socialTag}</span>
                            </div>
                          </div>

                          <div className="meal-card-right">
                            <div className="meal-emoji-badge">{avgEmoji}</div>
                            <div className="meal-score-bubble">{meal.overallScore}</div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Add another meal for this day - ONLY if it is today */}
                    {isToday && (
                      <button
                        className="add-another-meal-btn"
                        onClick={() => handleAddMeal(day.dateStr, dayNum)}
                      >
                        + Add another meal
                      </button>
                    )}
                  </div>
                ) : (
                  // Empty day placeholder - ONLY shown for today due to filter
                  <div
                    className="empty-day-placeholder"
                    onClick={() => handleAddMeal(day.dateStr, dayNum)}
                  >
                    <span className="empty-plus">+</span>
                    <span className="empty-text">No meals logged for today</span>
                    <span className="empty-action">Tap to log meal & earn Points</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
export default CalendarPage;
